/**
 * Buffer Bridge — Maps between ContentItem (frontend display) and ContentBuffer (backend persistence)
 *
 * ContentItem is the UI-facing type used in BufferSyncContext for rendering.
 * ContentBuffer (via BufferContentData in electron.d.ts) is the persistence type stored in PostgreSQL.
 *
 * @module @humanizer/studio/utils/buffer-bridge
 */

import type { ContentItem } from '../contexts/BufferSyncContext';
import type { BufferContentData } from '../types/electron';

/**
 * Convert ContentItem[] (frontend display) → BufferContentData partial (backend persistence)
 *
 * Used when saving UI content to the buffer service.
 * Takes the first item as the primary content (single-buffer model).
 */
export function contentItemToBufferData(
  item: ContentItem,
  existingBuffer?: BufferContentData | null
): Partial<BufferContentData> & { id: string } {
  const text = item.text;
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  const id = existingBuffer?.id ?? crypto.randomUUID();

  return {
    id,
    // contentHash: preserved from existing or computed by main process on save
    contentHash: existingBuffer?.contentHash ?? '',
    text,
    wordCount,
    format: existingBuffer?.format ?? 'markdown',
    state: existingBuffer?.state ?? 'staged',
    origin: existingBuffer?.origin ?? {
      sourceType: item.metadata.source?.nodeId ? 'archive' : 'manual',
      sourceNodeId: item.metadata.source?.nodeId,
      sourcePlatform: item.metadata.source?.type,
      author: item.metadata.author,
      authorRole: item.metadata.authorRole,
      metadata: {
        threadId: item.metadata.source?.threadId,
        threadTitle: item.metadata.source?.threadTitle,
      },
    },
    provenanceChain: existingBuffer?.provenanceChain ?? {
      id: crypto.randomUUID(),
      rootBufferId: id,
      currentBufferId: id,
      operations: [],
      branch: { name: 'main', isMain: true },
      transformationCount: 0,
    },
    createdAt: existingBuffer?.createdAt ?? Date.now(),
    updatedAt: Date.now(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// THREAD SERIALIZATION — multi-message round-trip via HTML comment markers
// ═══════════════════════════════════════════════════════════════════════════

/** Marker prefix for message boundaries in serialized buffer text */
const MSG_MARKER_PREFIX = '<!-- msg:';
const MSG_MARKER_REGEX = /^<!-- msg:(\d+) role:(\w+)(?: nodeId:(\S+))? -->\n/;

/**
 * Serialize ContentItem[] → single buffer text with message markers.
 *
 * HTML comments don't render in markdown preview but can be parsed back.
 * Format: `<!-- msg:0 role:user nodeId:abc123 -->\nMessage text here`
 */
export function contentItemsToBufferText(items: ContentItem[]): string {
  if (items.length <= 1) {
    return items[0]?.text ?? '';
  }

  return items.map((item, i) => {
    const role = item.metadata.authorRole ?? 'user';
    const nodeId = item.metadata.source?.nodeId ?? '';
    const marker = nodeId
      ? `${MSG_MARKER_PREFIX}${i} role:${role} nodeId:${nodeId} -->`
      : `${MSG_MARKER_PREFIX}${i} role:${role} -->`;
    return `${marker}\n${item.text}`;
  }).join('\n\n');
}

/**
 * Deserialize buffer text with markers → ContentItem[].
 *
 * Falls back to a single ContentItem if no markers are found (e.g. plain text buffers).
 */
export function bufferTextToContentItems(
  text: string,
  origin: BufferContentData['origin']
): ContentItem[] {
  // Check if text contains message markers
  if (!text.includes(MSG_MARKER_PREFIX)) {
    // Single message — return as-is
    return [{
      id: crypto.randomUUID(),
      type: 'archive-node',
      text,
      metadata: {
        source: origin.sourceNodeId ? {
          type: origin.sourcePlatform ?? origin.sourceType ?? 'archive',
          path: [(origin.metadata?.threadTitle as string) ?? 'Archive', origin.sourceNodeId],
          nodeId: origin.sourceNodeId,
          threadId: origin.metadata?.threadId as string | undefined,
          threadTitle: origin.metadata?.threadTitle as string | undefined,
        } : undefined,
        author: origin.author,
        authorRole: origin.authorRole,
        timestamp: Date.now(),
        wordCount: text.split(/\s+/).filter(Boolean).length,
      },
    }];
  }

  // Split on double-newline followed by marker
  const parts = text.split(/\n\n(?=<!-- msg:\d+)/);

  const items: ContentItem[] = [];

  for (const part of parts) {
    const match = part.match(MSG_MARKER_REGEX);
    if (match) {
      const [fullMatch, , role, nodeId] = match;
      const msgText = part.slice(fullMatch.length);
      items.push({
        id: crypto.randomUUID(),
        type: 'archive-node',
        text: msgText,
        metadata: {
          source: {
            type: origin.sourcePlatform ?? origin.sourceType ?? 'archive',
            path: [(origin.metadata?.threadTitle as string) ?? 'Archive', nodeId ?? ''],
            nodeId: nodeId ?? undefined,
            threadId: origin.metadata?.threadId as string | undefined,
            threadTitle: origin.metadata?.threadTitle as string | undefined,
          },
          authorRole: role as ContentItem['metadata']['authorRole'],
          timestamp: Date.now(),
          wordCount: msgText.split(/\s+/).filter(Boolean).length,
        },
      });
    } else {
      // Unmarked text block — treat as single item
      items.push({
        id: crypto.randomUUID(),
        type: 'text',
        text: part,
        metadata: {
          timestamp: Date.now(),
          wordCount: part.split(/\s+/).filter(Boolean).length,
        },
      });
    }
  }

  return items.length > 0 ? items : [{
    id: crypto.randomUUID(),
    type: 'text',
    text,
    metadata: { timestamp: Date.now(), wordCount: text.split(/\s+/).filter(Boolean).length },
  }];
}

/**
 * Convert BufferContentData (backend) → ContentItem (frontend display)
 *
 * Used when loading persisted buffers for UI rendering.
 */
export function bufferDataToContentItem(buffer: BufferContentData): ContentItem {
  const sourceType = buffer.origin.sourcePlatform ?? buffer.origin.sourceType ?? 'buffer';
  const nodeId = buffer.origin.sourceNodeId;
  const threadId = buffer.origin.metadata?.threadId as string | undefined;
  const threadTitle = buffer.origin.metadata?.threadTitle as string | undefined;

  // Determine ContentItem type from buffer origin
  let type: ContentItem['type'] = 'text';
  if (buffer.origin.sourceType === 'archive') {
    type = 'archive-node';
  } else if (buffer.origin.sourceType === 'generated') {
    type = 'transformed';
  }

  return {
    id: buffer.id,
    type,
    text: buffer.text,
    metadata: {
      source: nodeId ? {
        type: sourceType,
        path: [threadTitle ?? 'Archive', nodeId],
        nodeId,
        threadId,
        threadTitle,
      } : undefined,
      author: buffer.origin.author,
      authorRole: buffer.origin.authorRole,
      timestamp: buffer.createdAt,
      wordCount: buffer.wordCount,
      bufferId: buffer.id,
      bufferState: buffer.state,
    },
  };
}
